export interface CadStyleSubLayer {
  type: 'line' | 'symbol' | 'fill' | 'circle';
  'source-layer': string;
  id: string;
  layerID?: string;
  filter?: any[];
  paint?: Record<string, any>;
  layout?: Record<string, any>;
  minzoom?: number;
  maxzoom?: number;
}

export interface CadMapStyle {
  layers: CadStyleSubLayer[];
}

export interface CategoryColorMapping {
  value: string;
  label?: string;
  color: string;
}

export interface VectorStyleConfig {
  // Mode
  symbologyMode: 'single' | 'categorized';

  // Single color mode
  singleColor: string;

  // Categorized mode
  categoryField?: string;
  categories?: CategoryColorMapping[];
  fallbackColor?: string;

  // Polygon / Fill
  fillOpacity: number; // e.g. 0.05
  outlineWidth: number; // e.g. 1.5
  outlineColor?: string;

  // Line
  lineWidth: number; // e.g. 2.0
  lineOpacity: number; // e.g. 1.0
  linePattern?: 'solid' | 'dashed' | 'dotted';

  // Point / Circle
  pointRadius: number; // e.g. 6.0
  pointColor?: string;
  pointStrokeColor?: string;
  pointStrokeWidth?: number;

  // Labels
  showLabels: boolean;
  labelMode: 'single' | 'template';
  labelField?: string;
  labelTemplate?: string; // e.g. "{so_thua}/{dien_tich}\n{chu_su_dung}"
  fontSize: number; // e.g. 12
  textColor: string; // e.g. '#0F172A'
  haloColor: string; // e.g. '#FFFFFF'
  haloWidth: number; // e.g. 1.5
  allowOverlap: boolean; // default false
  minZoom?: number; // minzoom for labels e.g. 0 or 12
}

export const DEFAULT_VECTOR_STYLE_CONFIG: VectorStyleConfig = {
  symbologyMode: 'single',
  singleColor: '#F97316',
  fallbackColor: '#F97316',
  fillOpacity: 0.05,
  outlineWidth: 1.5,
  outlineColor: '#F97316',
  lineWidth: 2.0,
  lineOpacity: 1.0,
  linePattern: 'solid',
  pointRadius: 6.0,
  pointColor: '#F97316',
  pointStrokeColor: '#FFFFFF',
  pointStrokeWidth: 2.0,
  showLabels: true,
  labelMode: 'single',
  labelField: 'name',
  labelTemplate: '{name}',
  fontSize: 12,
  textColor: '#0F172A',
  haloColor: '#FFFFFF',
  haloWidth: 1.5,
  allowOverlap: false,
  minZoom: 0,
};

export function normalizeHexColor(c?: string): string {
  if (!c) return '38bdf8';
  let clean = c.trim().toLowerCase();
  if (clean.startsWith('#')) {
    clean = clean.slice(1);
  }
  if (clean.length === 3) {
    clean = clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2];
  }
  clean = clean.replace(/[^0-9a-f]/g, '');
  if (!clean || clean.length < 6) {
    return '38bdf8';
  }
  return clean.slice(0, 6);
}

export class CadStyleFactory {
  public static normalizeHexColor = normalizeHexColor;

  /**
   * Sort layers by natural numerical order (Level_1, Level_2, ..., Level_10, ..., Level_63)
   */
  public static sortLayersNaturally(layers: string[]): string[] {
    return [...layers].sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ''), 10);
      const numB = parseInt(b.replace(/\D/g, ''), 10);
      if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
        return numA - numB;
      }
      const aIsMask = a.toLowerCase().includes('che') || a.toLowerCase().includes('mask');
      const bIsMask = b.toLowerCase().includes('che') || b.toLowerCase().includes('mask');
      if (aIsMask && !bIsMask) return -1;
      if (!aIsMask && bIsMask) return 1;

      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  /**
   * Sort CAD layers by MicroStation Display Priority (-500 to +500).
   * Lower priority rendered first (bottom), higher priority rendered last (on top).
   */
  public static sortCadLayersByPriority(
    layers: string[],
    layerPriorities: Record<string, number> = {}
  ): string[] {
    return [...layers].sort((a, b) => {
      const getPrio = (name: string): number => {
        if (layerPriorities[name] !== undefined) {
          return layerPriorities[name];
        }
        const altName = name.replace(/\s+/g, '_');
        if (layerPriorities[altName] !== undefined) {
          return layerPriorities[altName];
        }
        const rawName = name.replace(/^Level_/, 'Level ');
        if (layerPriorities[rawName] !== undefined) {
          return layerPriorities[rawName];
        }
        const lower = name.toLowerCase();
        if (lower.includes('che') || lower.includes('mask')) return -310;
        return 0;
      };

      const pA = getPrio(a);
      const pB = getPrio(b);
      if (pA !== pB) {
        return pA - pB;
      }
      return this.sortLayersNaturally([a, b])[0] === a ? -1 : 1;
    });
  }

  /**
   * Helper to format Level name into source-layer name for Vector Tiles
   */
  public static getSourceLayerName(level: string): string {
    return level.startsWith('Level_') || !/^\d+$/.test(level) ? level : `Level_${level}`;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 1. CAD STYLE: Chuyên sâu cho DGN, DWG, DXF (Style theo thuộc tính CAD)
  // ─────────────────────────────────────────────────────────────────────────────

  public static createLevelStyleSubLayers(
    fileId: string,
    level: string,
    color?: string,
    heightDivisor: number = 1,
    idPrefix?: string
  ): CadStyleSubLayer[] {
    const sourceLayer = this.getSourceLayerName(level);
    const resolvedColor = color || '#38BDF8';
    const prefix = idPrefix !== undefined ? idPrefix : '';

    // ── Layer 1: Base wash (Chỉ đổ màu nền khi polygon có fill thực sự) ─────────
    const fillBaseLayerId = `${prefix}cadFillBase_${fileId}_${level}`;
    const fillBaseLayer: CadStyleSubLayer = {
      type: 'fill',
      'source-layer': sourceLayer,
      id: fillBaseLayerId,
      layerID: fillBaseLayerId,
      filter: [
        'all',
        ['==', '$type', 'Polygon'],
        ['!=', 'has_vector_hatch', 1],
        [
          'any',
          ['==', 'has_fill', 1],
          ['all', ['has', 'fill'], ['!=', 'fill', '']],
          ['all', ['has', 'fill_color'], ['!=', 'fill_color', '']],
          ['==', 'pattern_name', 'SOLID'],
          ['==', 'pattern_name', 'solid'],
        ],
      ],
      paint: {
        'fill-color': [
          'coalesce',
          ['get', 'fill'],
          ['get', 'fill_color'],
          ['get', 'color_hex'],
          resolvedColor,
        ],
        'fill-opacity': 1.0,
      },
    };

    // ── Layer 1.5: Hit-test surface (Trong suốt 100%, để bắt tap/click toàn diện tích parcel) ──
    const hitTestLayerId = `${prefix}cadHit_${fileId}_${level}`;
    const hitTestLayer: CadStyleSubLayer = {
      type: 'fill',
      'source-layer': sourceLayer,
      id: hitTestLayerId,
      layerID: hitTestLayerId,
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': '#000000',
        'fill-opacity': 0.0,
      },
    };

    // ── Layer 2: CAD Line stroke & boundaries ─────────────────────────────────
    const lineLayerId = `${prefix}cadLine_${fileId}_${level}`;
    const lineLayer: CadStyleSubLayer = {
      type: 'line',
      'source-layer': sourceLayer,
      id: lineLayerId,
      layerID: lineLayerId,
      paint: {
        'line-color': [
          'coalesce',
          ['get', 'stroke'],
          ['get', 'color_hex'],
          ['get', 'color'],
          resolvedColor,
        ],
        'line-width': [
          'case',
          // CAD ByLevel or invalid sentinel weights (e.g. 255) clamp to default hairline
          ['>=', ['coalesce', ['get', 'strokeWidth'], ['get', 'stroke-width'], ['get', 'weight'], 0], 32],
          0.75,
          ['>', ['coalesce', ['get', 'strokeWidth'], ['get', 'stroke-width'], ['get', 'weight'], 0], 0],
          ['min', 6, ['coalesce', ['get', 'strokeWidth'], ['get', 'stroke-width'], ['get', 'weight'], 0.75]],
          0.75,
        ],
      },
      layout: {
        'line-cap': 'butt',
        'line-join': 'miter',
      },
    };

    // Real-world height in ground meters (CAD text scaling)
    const heightInMeters: any[] = [
      'max',
      0.5,
      ['to-number', ['coalesce', ['get', 'height'], 2.0], 2.0],
    ];

    // ── Layer 3: CAD Text annotations ──────────────────────────────────────────
    const symbolLayerId = `${prefix}cadText_${fileId}_${level}`;
    const symbolLayer: CadStyleSubLayer = {
      type: 'symbol',
      'source-layer': sourceLayer,
      id: symbolLayerId,
      layerID: symbolLayerId,
      minzoom: 12,
      filter: [
        'any',
        ['has', 'Text'],
        ['has', 'text'],
        ['has', 'Label'],
        ['has', 'label'],
        ['has', 'name'],
      ],
      paint: {
        'text-color': [
          'coalesce',
          ['get', 'text-color'],
          ['get', 'color_hex'],
          ['get', 'color'],
          '#000000',
        ],
        'text-halo-color': 'rgba(255, 255, 255, 0.85)',
        'text-halo-width': 1.0,
      },
      layout: {
        'text-field': [
          'coalesce',
          ['get', 'Text'],
          ['get', 'text'],
          ['get', 'Label'],
          ['get', 'label'],
          ['get', 'name'],
          '',
        ],
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-size': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          0,  ['*', 0.0,      heightInMeters],
          4,  ['*', 0.000052, heightInMeters],
          6,  ['*', 0.000838, heightInMeters],
          8,  ['*', 0.00335,  heightInMeters],
          10, ['*', 0.0134,   heightInMeters],
          12, ['*', 0.0532,   heightInMeters],
          14, ['*', 0.2132,   heightInMeters],
          16, ['*', 0.8526,   heightInMeters],
          18, ['*', 3.41,     heightInMeters],
          20, ['*', 13.64,    heightInMeters],
          22, ['*', 54.56,    heightInMeters],
        ],
        'text-rotate': ['-', 360, ['to-number', ['coalesce', ['get', 'rotation_deg'], 0], 0]],
        'text-keep-upright': true,
        'text-max-width': 500,
        'text-rotation-alignment': 'map',
        'text-pitch-alignment': 'map',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-anchor': [
          'case',
          ['==', ['to-number', ['coalesce', ['get', 'justification'], 0], 0], 1], 'bottom',
          ['==', ['to-number', ['coalesce', ['get', 'justification'], 0], 0], 2], 'bottom-right',
          ['==', ['to-number', ['coalesce', ['get', 'justification'], 0], 0], 6], 'center',
          'bottom-left',
        ],
        'text-offset': [0.0, 0.0],
      },
    };

    return [hitTestLayer, fillBaseLayer, lineLayer, symbolLayer];
  }

  public static createDefaultCadStyles(
    fileId: string,
    layers: string[],
    layerColors: Record<string, string> = {},
    heightDivisor: number = 1,
    layerPriorities: Record<string, number> = {},
    idPrefix?: string
  ): CadMapStyle {
    const allSubLayers: CadStyleSubLayer[] = [];

    const getPrio = (name: string): number => {
      if (layerPriorities[name] !== undefined) {
        return layerPriorities[name];
      }
      const altName = name.replace(/\s+/g, '_');
      if (layerPriorities[altName] !== undefined) {
        return layerPriorities[altName];
      }
      const rawName = name.replace(/^Level_/, 'Level ');
      if (layerPriorities[rawName] !== undefined) {
        return layerPriorities[rawName];
      }
      const lower = name.toLowerCase();
      if (lower.includes('che') || lower.includes('mask')) return -310;
      return 0;
    };

    const typeOrder: Record<string, number> = {
      fill: 1,
      circle: 2,
      line: 3,
      symbol: 4,
    };

    for (const lvl of layers) {
      const color = layerColors[lvl];
      const lvlSubLayers = this.createLevelStyleSubLayers(fileId, lvl, color, heightDivisor, idPrefix);
      allSubLayers.push(...lvlSubLayers);
    }

    allSubLayers.sort((a, b) => {
      const lvlA = a['source-layer'] || '';
      const lvlB = b['source-layer'] || '';
      const pA = getPrio(lvlA);
      const pB = getPrio(lvlB);

      // 1. Primary order: MicroStation display priority (-500 to +500)
      if (pA !== pB) {
        return pA - pB;
      }

      // 2. Secondary order (when priority ties): geometry type class
      //    fill < circle < line < symbol (text)
      const tA = typeOrder[a.type] || 3;
      const tB = typeOrder[b.type] || 3;
      if (tA !== tB) {
        return tA - tB;
      }

      // 3. Tertiary order (within same type class): natural numerical order of level
      const numA = parseInt(lvlA.replace(/\D/g, ''), 10);
      const numB = parseInt(lvlB.replace(/\D/g, ''), 10);
      if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
        return numA - numB;
      }

      return lvlA.localeCompare(lvlB, undefined, { numeric: true, sensitivity: 'base' });
    });

    return {
      layers: allSubLayers,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. PRO GIS STYLE GENERATOR (KML, KMZ, GeoJSON, Vector MBTiles)
  // ─────────────────────────────────────────────────────────────────────────────

  public static buildLabelTextFieldExpression(template: string): any {
    if (!template || typeof template !== 'string') {
      return ['coalesce', ['get', 'name'], ['get', 'Name'], ''];
    }

    const tokens = template.split(/(\{[^}]+\})/g).filter(Boolean);

    if (tokens.length === 0) {
      return '';
    }

    if (tokens.length === 1 && !tokens[0].startsWith('{')) {
      return tokens[0];
    }

    const concatArgs: any[] = [];
    for (const token of tokens) {
      if (token.startsWith('{') && token.endsWith('}')) {
        const fieldName = token.slice(1, -1).trim();
        if (fieldName) {
          concatArgs.push(['to-string', ['coalesce', ['get', fieldName], '']]);
        }
      } else {
        concatArgs.push(token);
      }
    }

    if (concatArgs.length === 1) {
      return concatArgs[0];
    }

    return ['concat', ...concatArgs];
  }

  public static buildColorExpression(
    mode: 'single' | 'categorized',
    singleColor: string,
    categoryField?: string,
    categories?: CategoryColorMapping[],
    fallbackColor: string = '#F97316'
  ): any {
    if (mode === 'categorized' && categoryField && Array.isArray(categories) && categories.length > 0) {
      const matchArgs: any[] = [
        ['to-string', ['coalesce', ['get', categoryField], '']],
      ];

      for (const cat of categories) {
        if (cat.value !== undefined && cat.value !== null && cat.color) {
          matchArgs.push(String(cat.value));
          matchArgs.push(cat.color);
        }
      }

      matchArgs.push(fallbackColor || singleColor || '#F97316');
      return ['match', ...matchArgs];
    }

    return singleColor || fallbackColor || '#F97316';
  }

  public static createCustomGisStyles(
    fileId: string,
    layers: string[],
    config: Partial<VectorStyleConfig> = {},
    idPrefix?: string
  ): CadMapStyle {
    const fullConfig: VectorStyleConfig = {
      ...DEFAULT_VECTOR_STYLE_CONFIG,
      ...config,
    };

    const prefix = idPrefix !== undefined ? idPrefix : '';
    const effectiveLayers = layers.length > 0 ? layers : ['default'];
    const subLayers: CadStyleSubLayer[] = [];

    const mainColorExpr = this.buildColorExpression(
      fullConfig.symbologyMode,
      fullConfig.singleColor,
      fullConfig.categoryField,
      fullConfig.categories,
      fullConfig.fallbackColor
    );

    const outlineColorExpr = fullConfig.outlineColor || mainColorExpr;
    const pointColorExpr = fullConfig.pointColor || mainColorExpr;

    let lineDashArray: number[] | undefined = undefined;
    if (fullConfig.linePattern === 'dashed') {
      lineDashArray = [4, 2];
    } else if (fullConfig.linePattern === 'dotted') {
      lineDashArray = [1, 2];
    }

    let textFieldExpr: any;
    if (fullConfig.labelMode === 'template' && fullConfig.labelTemplate) {
      textFieldExpr = this.buildLabelTextFieldExpression(fullConfig.labelTemplate);
    } else if (fullConfig.labelField) {
      textFieldExpr = ['to-string', ['coalesce', ['get', fullConfig.labelField], '']];
    } else {
      textFieldExpr = ['coalesce', ['get', 'name'], ['get', 'Name'], ['get', 'text'], ['get', 'Text'], ''];
    }

    for (const layer of effectiveLayers) {
      const sourceLayer = this.getSourceLayerName(layer);

      // 1. Polygon Fill
      const fillId = `${prefix}gisFill_${fileId}_${layer}`;
      subLayers.push({
        type: 'fill',
        'source-layer': sourceLayer,
        id: fillId,
        layerID: fillId,
        filter: ['==', '$type', 'Polygon'],
        paint: {
          'fill-color': mainColorExpr,
          'fill-opacity': fullConfig.fillOpacity,
          'fill-outline-color': outlineColorExpr,
        },
      });

      // 2. Line / Outlines
      const linePaint: Record<string, any> = {
        'line-color': outlineColorExpr,
        'line-width': fullConfig.lineWidth || 2.0,
        'line-opacity': fullConfig.lineOpacity ?? 1.0,
      };
      if (lineDashArray) {
        linePaint['line-dasharray'] = lineDashArray;
      }

      const lineId = `${prefix}gisLine_${fileId}_${layer}`;
      subLayers.push({
        type: 'line',
        'source-layer': sourceLayer,
        id: lineId,
        layerID: lineId,
        paint: linePaint,
        layout: {
          'line-cap': 'butt',
          'line-join': 'miter',
        },
      });

      // 3. Point / Circle
      const circleId = `${prefix}gisCircle_${fileId}_${layer}`;
      subLayers.push({
        type: 'circle',
        'source-layer': sourceLayer,
        id: circleId,
        layerID: circleId,
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': fullConfig.pointRadius || 6.0,
          'circle-color': pointColorExpr,
          'circle-stroke-width': fullConfig.pointStrokeWidth || 2.0,
          'circle-stroke-color': fullConfig.pointStrokeColor || '#FFFFFF',
        },
      });

      // 4. Label / Symbol (Optional)
      if (fullConfig.showLabels) {
        const textId = `${prefix}gisText_${fileId}_${layer}`;
        subLayers.push({
          type: 'symbol',
          'source-layer': sourceLayer,
          id: textId,
          layerID: textId,
          minzoom: fullConfig.minZoom ?? 0,
          paint: {
            'text-color': fullConfig.textColor || '#0F172A',
            'text-halo-color': fullConfig.haloColor || '#FFFFFF',
            'text-halo-width': fullConfig.haloWidth || 1.5,
          },
          layout: {
            'text-field': textFieldExpr,
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-size': fullConfig.fontSize || 12,
            'text-anchor': 'top',
            'text-offset': [0, 0.8],
            'text-allow-overlap': fullConfig.allowOverlap === true,
            'text-ignore-placement': fullConfig.allowOverlap === true,
            'text-optional': true,
          },
        });
      }
    }

    return {
      layers: subLayers,
    };
  }

  public static createSimpleGisStyles(
    fileId: string,
    layers: string[],
    defaultColor: string = '#F97316',
    idPrefix?: string
  ): CadMapStyle {
    return this.createCustomGisStyles(fileId, layers, {
      singleColor: defaultColor,
      outlineColor: defaultColor,
      pointColor: defaultColor,
    }, idPrefix);
  }

  public static createAutoStyles(
    fileId: string,
    fileName: string,
    layers: string[],
    layerColors: Record<string, string> = {},
    heightDivisor: number = 1,
    savedStyleConfig?: VectorStyleConfig,
    layerPriorities: Record<string, number> = {},
    idPrefix?: string
  ): CadMapStyle {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    const isCad = ext === 'dgn' || ext === 'dwg' || ext === 'dxf';

    if (isCad) {
      return this.createDefaultCadStyles(fileId, layers, layerColors, heightDivisor, layerPriorities, idPrefix);
    } else {
      if (savedStyleConfig) {
        return this.createCustomGisStyles(fileId, layers, savedStyleConfig, idPrefix);
      }
      const defaultColor = '#F97316';
      return this.createSimpleGisStyles(fileId, layers, defaultColor, idPrefix);
    }
  }
}

export default CadStyleFactory;
